import { ApolloError, useMutation } from '@apollo/client';
import { tracking } from 'App/Tracking';
import { SET_USER_SELECTED_LOCATION } from './setUserSelectedLocation';
import {
  SetUserSelectedLocation,
  SetUserSelectedLocationVariables,
} from './__generated__/SetUserSelectedLocation';

export type SetUserSelectedLocationSource = 'help' | 'account';

interface UseSetUserSelectedLocationParams {
  source: SetUserSelectedLocationSource;
  onCompleted?(success: boolean): void | Promise<void>;
  onError?(error: ApolloError): void;
}

export const useSetUserSelectedLocation = ({
  source,
  onCompleted,
  onError,
}: UseSetUserSelectedLocationParams) => {
  const [mutate, { loading }] = useMutation<
    SetUserSelectedLocation,
    SetUserSelectedLocationVariables
  >(SET_USER_SELECTED_LOCATION);

  const setLocation = async (
    countryCode: string,
    countryName?: string,
  ): Promise<{ success: boolean }> => {
    try {
      const { data } = await mutate({
        variables: { input: { countryCode } },
      });

      const success = Boolean(data?.setUserSelectedLocation?.success);

      // The mutation completed without an Apollo error - track and hand the
      // payload's `success` to the caller, which decides how to handle a
      // `success: false` response. `onError` is reserved for genuine
      // ApolloErrors (network/GraphQL failures) thrown by `mutate`.
      if (success) {
        tracking.track('user_selected_location_updated', {
          countryName,
          countryCode,
          source,
        });
      }

      if (onCompleted) {
        await onCompleted(success);
      }

      return { success };
    } catch (error) {
      // The only awaited call in the try is `mutate`, which rejects with an
      // ApolloError, so the cast is safe here.
      if (onError) {
        onError(error as ApolloError);
      }

      return { success: false };
    }
  };

  return { setLocation, loading };
};
